@layer refine.base {
  :host {
    --step-indicator-size: var(--size-8);
    --step-font-size: var(--text-sm);
    --step-gap: var(--size-2);
    display: flex;
    flex: 1 1 0%;
    min-width: 0;
  }

  .step {
    position: relative;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    width: 100%;
    list-style: none;
  }

  /* Horizontal (default) layout centers the indicator above the label in each equal-width
     column (see `.control`/`.content` below) so the indicator sits at a predictable, constant
     horizontal position — the midpoint of its own step — regardless of label length. The full
     line between two indicators is drawn as *two* halves — this step's own `.connector-leading`
     (its own left edge to its own indicator) and `.connector-trailing` (its own indicator to its
     own right edge) — each confined entirely inside this step's own box. Reaching a single
     connector backward into the previous step's box (an earlier attempt) doesn't work: `ore-step`
     is its own custom element/shadow root, so a later sibling's positioned content always paints
     in front of an earlier sibling's regardless of internal stacking, and `.control`'s own hover
     background only covers its own box — so the part of the line sitting outside it looked like
     a stray line poking out next to the hover highlight instead of being part of it. */
  .connector {
    position: absolute;
    top: calc(var(--size-1) + var(--step-indicator-size) / 2);
    width: calc(50% - var(--step-indicator-size) / 2);
    height: var(--stepper-connector-size, 2px);
    background: var(--stepper-connector-color, var(--color-contrast-200));
    border-radius: var(--rounded-full);
    transform: translateY(-50%);
    transition: background var(--transition-fast);
  }

  .connector-leading {
    left: 0;
  }

  .connector-trailing {
    right: 0;
  }

  /* No preceding/following step to connect to. */
  :host(:first-of-type) .connector-leading {
    display: none;
  }

  :host(:last-of-type) .connector-trailing {
    display: none;
  }

  .control {
    all: unset;

    /* Declared after `all: unset` (which resets `position` back to `static`), not before it.
       Puts `.control` in the same paint order as `.connector` (also positioned) instead of
       `.connector` — being absolutely positioned — automatically painting above all of this
       *static*, normal-flow content regardless of DOM order. `.control` comes after
       `.connector` in the markup, so with both positioned it (and the opaque indicator inside
       it) now correctly paints over the connector line instead of the line cutting across
       every indicator. */
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: var(--step-gap);
    align-items: center;
    width: 100%;
    min-width: 0;
    padding: var(--size-1);
    font-family: inherit;
    cursor: default;
    border-radius: var(--rounded-lg);
  }

  button.control {
    min-height: var(--_touch-target, auto);
    cursor: pointer;
    transition: background var(--transition-fast);
  }

  button.control:focus-visible {
    outline: var(--border-2) solid var(--_theme-base, var(--color-contrast-500));
    outline-offset: var(--border-2);
  }

  button.control[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .indicator {
    /* Without this, the `border` below adds on top of `width`/`height` instead of being
       included in them, rendering the indicator ~2 * border-width larger per axis than
       `--step-indicator-size` — every connector position calc using that variable to line up
       with the indicator's true center/edge (especially the vertical connector's horizontal
       `left`, which has no flexbox centering to fall back on) would then be off by that much. */
    box-sizing: border-box;
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: var(--step-indicator-size);
    height: var(--step-indicator-size);
    font-size: var(--step-font-size);
    font-weight: var(--font-semibold);
    color: var(--color-contrast-500);
    background: var(--color-contrast-100);
    border: var(--border-2) solid var(--color-contrast-200);
    border-radius: var(--rounded-full);
    transition:
      background var(--transition-fast),
      color var(--transition-fast),
      border-color var(--transition-fast);
  }

  .icon-slot[hidden] {
    display: none;
  }

  .number {
    line-height: 1;
  }

  .content {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    min-width: 0;
    text-align: center;
  }

  .label {
    display: flex;
    gap: var(--size-1);
    align-items: baseline;
    justify-content: center;
    min-width: 0;
    font-size: var(--step-font-size);
    font-weight: var(--font-medium);
    color: var(--color-contrast-700);
  }

  .label-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .optional-hint {
    flex: 0 0 auto;
    font-size: var(--text-xs);
    font-weight: var(--font-normal);
    color: var(--color-contrast-400);
  }

  .description {
    font-size: var(--text-xs);
    color: var(--color-contrast-500);
  }

  .description[hidden] {
    display: none;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    white-space: nowrap;
    border-width: 0;
    clip-path: inset(50%);
  }
}

@layer refine.states {
  :host([current]) .indicator {
    color: var(--_theme-contrast, var(--color-canvas));
    background: var(--_theme-base, var(--color-contrast-800));
    border-color: var(--_theme-base, var(--color-contrast-800));
  }

  :host([current]) .label {
    font-weight: var(--font-semibold);
    color: var(--color-contrast-900);
  }

  :host([completed]) .indicator {
    color: var(--_theme-contrast, var(--color-canvas));
    background: var(--_theme-focus, var(--color-contrast-600));
    border-color: var(--_theme-focus, var(--color-contrast-600));
  }

  /* A completed step colors both the segment leading into it and the one trailing out of it
     (progress has fully passed through); the current step only colors the leading segment
     (progress has arrived, but not yet moved past it into the next step). */
  :host([completed]) .connector,
  :host([current]) .connector-leading {
    background: var(--_theme-focus, var(--color-contrast-400));
  }

  :host([error]) .indicator {
    color: var(--color-canvas);
    background: var(--color-error);
    border-color: var(--color-error);
  }

  :host([error]) .label {
    color: var(--color-error);
  }

  :host([disabled]) .label,
  :host([disabled]) .description {
    color: var(--color-contrast-400);
  }

  :host([disabled]) .indicator {
    opacity: 0.6;
  }
}

@layer refine.sizes {
  :host([size='sm']) {
    --step-indicator-size: var(--size-6);
    --step-font-size: var(--text-xs);
    --step-gap: var(--size-1-5);
  }

  :host(:not([size])),
  :host([size='md']) {
    --step-indicator-size: var(--size-8);
    --step-font-size: var(--text-sm);
    --step-gap: var(--size-2);
  }

  :host([size='lg']) {
    --step-indicator-size: var(--size-10);
    --step-font-size: var(--text-base);
    --step-gap: var(--size-2-5);
  }

  @media (pointer: coarse) {
    :host(:not([size])),
    :host([size='sm']),
    :host([size='md']) {
      --step-indicator-size: var(--size-9);
      --step-font-size: var(--text-sm);
    }
  }
}

@layer refine.orientation {
  /* Vertical (mobile-friendly) layout: back to the inline icon-beside-label arrangement (more
     compact for narrow/mobile widths), with `padding-block-start` reserving room for a
     top-to-bottom connector into this step's own indicator instead of the horizontal
     center-to-center line above. */
  :host([orientation='vertical']) {
    flex: 0 0 auto;
    width: 100%;
  }

  :host([orientation='vertical']) .step {
    align-items: flex-start;
    padding-block-start: var(--stepper-gap, var(--size-4));
  }

  :host([orientation='vertical']:first-of-type) .step {
    padding-block-start: 0;
  }

  :host([orientation='vertical']) .control {
    flex-direction: row;
    align-items: flex-start;
  }

  :host([orientation='vertical']) .content {
    align-items: flex-start;
    text-align: left;
  }

  :host([orientation='vertical']) .label {
    justify-content: flex-start;
  }

  /* Same two-halves idea as horizontal, rotated: `.connector-leading` runs from this step's own
     top edge down into its indicator (through the `padding-block-start` gap reserved above).
     `.connector-trailing` runs from the *bottom* of this step's indicator down to this step's
     own bottom edge — using `top`+`bottom` (no fixed `height`) so it stretches to whatever this
     step's content actually needs, instead of a fixed length that only reached as far as the
     padding gap. A step with a longer description is taller, and its trailing segment grows
     with it — the next step's leading segment then only has to bridge the fixed padding gap,
     so the two halves always meet exactly at the boundary between the two steps regardless of
     how much content either one has. */
  :host([orientation='vertical']) .connector-leading {
    top: 0;
    left: calc(var(--size-1) + var(--step-indicator-size) / 2);
    width: var(--stepper-connector-size, 2px);
    height: calc(var(--stepper-gap, var(--size-4)) + var(--size-1));
    transform: translateX(-50%);
  }

  :host([orientation='vertical']) .connector-trailing {
    inset: calc(var(--stepper-gap, var(--size-4)) + var(--size-1) + var(--step-indicator-size)) auto 0
      calc(var(--size-1) + var(--step-indicator-size) / 2);
    width: var(--stepper-connector-size, 2px);
    height: auto;
    transform: translateX(-50%);
  }

  /* The first step's `padding-block-start` is reset to 0 above (no incoming line to make room
     for) — `.connector-trailing`'s `top` has to drop the same gap term here, or it starts
     lower than the indicator's true bottom edge and its `bottom: 0` counterpart can compute a
     negative (clamped to 0) height, hiding the segment entirely. */
  :host([orientation='vertical']:first-of-type) .connector-trailing {
    top: calc(var(--size-1) + var(--step-indicator-size));
  }
}

@media (forced-colors: active) {
  .indicator {
    border: 1px solid ButtonText;
  }

  :host([current]) .indicator,
  :host([completed]) .indicator {
    border: 1px solid Highlight;
  }
}
